
import { Component, Input, Output, EventEmitter, Inject, OnChanges, SimpleChanges, HostListener, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AdminService, Employee, Costcenter, Team } from '../../../core/services/admin.service';
import { AdminFeedbackService } from '../../../core/services/admin-feedback.service';
import { DialogService } from '../../../core/services/dialog.service';
import moment from 'moment';
import { formatCostcenterLabel } from '../../../shared/utils/costcenter.utils';
import {
  EMPLOYEE_ACTIVE_FILTER_OPTIONS,
  EMPLOYEE_TYPE_FILTER_OPTIONS,
  YES_NO_OPTIONS,
} from '../../../shared/constants/ui-options';
const { ROLE_OPTIONS } = require('../../../../../shared/roles.js');

@Component({
  selector: 'app-admin-employee-tab',
  standalone: false,
  template: require('./admin-employee-tab.component.html').default,
  styles: [require('!!raw-loader!sass-loader!./admin-employee-tab.component.scss').default],
})
export class AdminEmployeeTabComponent implements OnChanges {
  private readonly requiredCreateFields: Array<keyof Employee> = [
    'name',
    'loginname',
    'passwd',
    'start_date',
    'team_description',
    'boss_employee_id',
    'initial_salary',
  ];

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.btn-group')) {
      this.showMoreMenu = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.showMoreMenu = false;
  }

  @Input() employees: Employee[] = [];
  @Input() teams: Team[] = [];
  @Input() costcenters: Costcenter[] = [];
  @Output() dataUpdated = new EventEmitter<void>();

  // Filtered list
  filteredEmployees: Employee[] = [];
  selectableTeams: string[] = [];
  selectableBosses: Employee[] = [];
  costcenterOptions: Array<{ value: string | null; label: string }> = [];

  // Current selection
  currentIndex = 0;
  selectedEmployee: Employee | null = null;
  editEmployee: Partial<Employee> = {};
  initialEditEmployee: Partial<Employee> = {};
  originalId: number | null = null;

  // Moment.js date objects for app-datepicker
  startDateMoment: moment.Moment | null = null;
  stopDateMoment: moment.Moment | null = null;

  // New record mode
  isNew = false;

  // Filters
  activeFilter: 'all' | 'active' | 'inactive' = 'active';
  typeFilter: 'all' | 'kitsare' | 'uk' = 'kitsare';

  // Dirty flag
  isDirty = false;
  showPassword = false;

  // Dropdown menu toggle
  showMoreMenu = false;
  roleOptions = ROLE_OPTIONS;
  contractorOptions = YES_NO_OPTIONS;
  activeFilterOptions = EMPLOYEE_ACTIVE_FILTER_OPTIONS;
  typeFilterOptions = EMPLOYEE_TYPE_FILTER_OPTIONS;
  private pendingSelectionId: number | null = null;
  private pendingSelectionIndex: number | null = null;

  private destroyRef = inject(DestroyRef);

  constructor(
    @Inject(AdminService) private adminService: AdminService,
    @Inject(DialogService) private dialogService: DialogService,
    @Inject(AdminFeedbackService) private adminFeedback: AdminFeedbackService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['teams']) {
      this.updateSelectableTeams();
    }

    if (changes['employees']) {
      this.updateSelectableBosses();
    }

    if (changes['costcenters']) {
      this.updateCostcenterOptions();
    }

    if (changes['employees'] && this.employees.length > 0) {
      this.applyFilters();
    }
  }

  applyFilters(): void {
    let filtered = this.employees.filter((e) => e.employee_id !== 0);

    if (this.activeFilter === 'active') {
      filtered = filtered.filter((e) => e.stop_date === null);
    } else if (this.activeFilter === 'inactive') {
      filtered = filtered.filter((e) => e.stop_date !== null);
    }

    if (this.typeFilter === 'kitsare') {
      filtered = filtered.filter((e) => e.employee_number !== null);
    } else if (this.typeFilter === 'uk') {
      filtered = filtered.filter((e) => e.employee_number === null);
    }

    this.filteredEmployees = filtered;

    if (this.isNew || this.isDirty) {
      if (!this.isNew && this.selectedEmployee) {
        const selectedIndex = this.filteredEmployees.findIndex((e) => e.employee_id === this.selectedEmployee?.employee_id);
        this.currentIndex = selectedIndex >= 0 ? selectedIndex : 0;
      } else if (this.filteredEmployees.length === 0) {
        this.currentIndex = 0;
      } else {
        this.currentIndex = Math.min(this.currentIndex, this.filteredEmployees.length - 1);
      }

      this.pendingSelectionId = null;
      this.pendingSelectionIndex = null;
      return;
    }

    if (this.filteredEmployees.length > 0) {
      const preferredId = this.pendingSelectionId ?? this.originalId ?? this.selectedEmployee?.employee_id ?? null;
      const preferredIndex =
        preferredId === null ? -1 : this.filteredEmployees.findIndex((e) => e.employee_id === preferredId);

      if (preferredIndex >= 0) {
        this.currentIndex = preferredIndex;
        this.selectEmployee(this.filteredEmployees[preferredIndex]);
      } else if (this.pendingSelectionIndex !== null) {
        const clampedIndex = Math.min(this.pendingSelectionIndex, this.filteredEmployees.length - 1);
        this.currentIndex = clampedIndex;
        this.selectEmployee(this.filteredEmployees[clampedIndex]);
      } else {
        this.currentIndex = 0;
        this.selectEmployee(this.filteredEmployees[0]);
      }
    } else {
      this.selectedEmployee = null;
      this.editEmployee = {};
      this.currentIndex = 0;
    }

    this.pendingSelectionId = null;
    this.pendingSelectionIndex = null;
  }

  private updateSelectableTeams(): void {
    this.selectableTeams = Array.from(new Set(this.teams.map((team) => team.description).filter(Boolean))).sort((a, b) =>
      a.localeCompare(b, 'sv')
    );
  }

  private updateSelectableBosses(): void {
    this.selectableBosses = this.employees
      .filter((employee) => employee.employee_id > 0 && employee.stop_date === null && String(employee.contractor || '').toUpperCase() !== 'J')
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name, 'sv'));
  }

  private updateCostcenterOptions(): void {
    this.costcenterOptions = [
      { value: null, label: 'Inget' },
      ...this.costcenters.map((cc) => ({
        value: String(cc.external_id),
        label: formatCostcenterLabel(cc),
      })),
    ];
  }

  selectEmployee(emp: Employee): void {
    this.selectedEmployee = emp;
    this.originalId = emp.employee_id;
    this.editEmployee = { ...emp };
    this.initialEditEmployee = { ...emp };
    this.startDateMoment = emp.start_date ? moment(emp.start_date) : null;
    this.stopDateMoment = emp.stop_date ? moment(emp.stop_date) : null;
    this.isNew = false;
    this.isDirty = false;
    this.showPassword = false;
  }

  onStartDateChange(newDate: moment.Moment): void {
    this.startDateMoment = newDate;
    this.editEmployee.start_date = newDate ? newDate.format('YYYY-MM-DD') : undefined;
    this.onFieldChange();
  }

  onStopDateChange(newDate: moment.Moment): void {
    this.stopDateMoment = newDate;
    this.editEmployee.stop_date = newDate ? newDate.format('YYYY-MM-DD') : null;
    this.onFieldChange();
  }

  onFilterChange(): void {
    this.applyFilters();
  }

  onEmployeeSelect(): void {
    if (this.selectedEmployee) {
      const index = this.filteredEmployees.findIndex(e => e.employee_id === this.selectedEmployee?.employee_id);
      if (index >= 0) {
        this.currentIndex = index;
      }
      this.selectEmployee(this.selectedEmployee);
    }
  }

  movePrev(): void {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.selectEmployee(this.filteredEmployees[this.currentIndex]);
    }
  }

  moveNext(): void {
    if (this.currentIndex < this.filteredEmployees.length - 1) {
      this.currentIndex++;
      this.selectEmployee(this.filteredEmployees[this.currentIndex]);
    }
  }

  addNew(): void {
    const defaultCostcenter = this.costcenters.find((cc) => cc.external_id === 200);

    this.isNew = true;
    this.selectedEmployee = null;
    this.originalId = null;
    this.editEmployee = {
      name: '',
      loginname: '',
      passwd: '',
      role: 'n',
      contractor: 'N',
      costcenter: defaultCostcenter ? String(defaultCostcenter.external_id) : null,
      start_date: new Date().toISOString().split('T')[0],
      team_description: null,
      boss_employee_id: null,
      initial_salary: null,
    };
    this.initialEditEmployee = { ...this.editEmployee };
    this.startDateMoment = this.editEmployee.start_date ? moment(this.editEmployee.start_date) : null;
    this.stopDateMoment = null;
    this.isDirty = false;
    this.showPassword = false;
  }

  save(): void {
    if (this.isNew) {
      this.adminService.createEmployee(this.editEmployee).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (result) => {
          this.pendingSelectionId = result?.insertId ?? null;
          this.isNew = false;
          this.isDirty = false;
          this.dataUpdated.emit();
        },
        error: (err) => this.adminFeedback.showCrudError('skapa', 'anställd', err),
      });
    } else if (this.originalId !== null) {
      this.adminService.updateEmployee(this.originalId, this.editEmployee).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.pendingSelectionId = this.originalId;
          this.isDirty = false;
          this.dataUpdated.emit();
        },
        error: (err) => this.adminFeedback.showCrudError('uppdatera', 'anställd', err),
      });
    }
  }

  cancel(): void {
    if (this.isNew) {
      this.isNew = false;
      if (this.filteredEmployees.length > 0) {
        this.selectEmployee(this.filteredEmployees[this.currentIndex]);
      } else {
        this.editEmployee = {};
        this.initialEditEmployee = {};
        this.startDateMoment = null;
        this.stopDateMoment = null;
      }
    } else if (this.selectedEmployee) {
      this.selectEmployee(this.selectedEmployee);
      return;
    }
    this.isDirty = false;
    this.showPassword = false;
  }

  async delete(): Promise<void> {
    if (this.originalId === null) {
      return;
    }

    const confirmed = await this.dialogService.confirm({
      title: 'Bekräfta borttagning',
      body: 'Är du säker på att du vill ta bort denna anställd?',
      confirmText: 'Ta bort',
      cancelText: 'Avbryt',
      variant: 'danger',
    });

    if (!confirmed) {
      return;
    }

    this.pendingSelectionIndex = this.currentIndex;
    this.adminService.deleteEmployee(this.originalId!).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.dataUpdated.emit();
      },
      error: (err) => this.adminFeedback.showCrudError('ta bort', 'anställd', err),
    });
  }

  duplicate(): void {
    if (!this.selectedEmployee) return;
    this.isNew = true;
    this.originalId = null;
    this.editEmployee = {
      ...this.selectedEmployee,
      employee_id: undefined,
      name: this.selectedEmployee.name + ' (kopia)',
    };
    this.initialEditEmployee = { ...this.selectedEmployee };
    this.startDateMoment = this.editEmployee.start_date ? moment(this.editEmployee.start_date) : null;
    this.stopDateMoment = this.editEmployee.stop_date ? moment(this.editEmployee.stop_date) : null;
    this.isDirty = true;
    this.showPassword = false;
  }

  onFieldChange(): void {
    this.isDirty = this.hasChanges();
  }

  isRequiredField(field: keyof Employee): boolean {
    return this.requiredCreateFields.includes(field);
  }

  isRequiredMissing(field: keyof Employee): boolean {
    if (!this.isNew || !this.isRequiredField(field)) {
      return false;
    }

    const value = this.editEmployee[field];
    return typeof value === 'string' ? value.trim() === '' : value == null;
  }

  hasMissingRequiredFields(): boolean {
    return this.requiredCreateFields.some((field) => this.isRequiredMissing(field));
  }

  hasInvalidDateRange(): boolean {
    const start = this.editEmployee.start_date;
    const stop = this.editEmployee.stop_date;
    return typeof start === 'string' && typeof stop === 'string' && start !== '' && stop !== '' && start > stop;
  }

  isFieldDirty(field: keyof Employee): boolean {
    return this.normalizeFieldValue(this.editEmployee[field]) !== this.normalizeFieldValue(this.initialEditEmployee[field]);
  }

  hasChanges(): boolean {
    const fields = new Set<keyof Employee>([
      ...Object.keys(this.editEmployee),
      ...Object.keys(this.initialEditEmployee),
    ] as Array<keyof Employee>);
    return Array.from(fields).some((field) => this.isFieldDirty(field));
  }

  private normalizeFieldValue(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }
    return String(value);
  }

  canSave(): boolean {
    if (!this.isDirty) {
      return false;
    }

    if (this.hasInvalidDateRange()) {
      return false;
    }

    if (!this.isNew) {
      return true;
    }

    return this.requiredCreateFields.every((field) => !this.isRequiredMissing(field));
  }

  canUndo(): boolean {
    return this.isDirty || this.isNew;
  }

  canAddNew(): boolean {
    return !this.isNew && !this.isDirty;
  }

  canOpenMoreMenu(): boolean {
    return !this.isNew && !this.isDirty && !!this.selectedEmployee;
  }

  protected readonly formatCostcenterLabel = formatCostcenterLabel;
}
